// components/private/order/ImportedByPicView.tsx
import { useQuery } from "@tanstack/react-query";
import { axiosClient } from "@/lib/axiosClient";
import { ShieldAlert, UserCheck, ArrowLeft, Info, Mail, Landmark } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

interface ImportedByPicViewProps {
    picId: number | string;
}

export const ImportedByPicView = ({ picId }: ImportedByPicViewProps) => {
    const navigate = useNavigate();

    const { data: picProfile, isLoading } = useQuery({
        queryKey: ["pic-profile", picId],
        queryFn: async () => {
            const { data } = await axiosClient.get(`/participants/${picId}`);
            return data.data;
        },
        enabled: !!picId,
    });

    if (isLoading) {
        return (
            <div className="w-full space-y-6">
                <Card className="w-full shadow-xl overflow-hidden border p-0">

                    {/* Header Skeleton */}
                    <div className="p-10 bg-slate-900">
                        <div className="flex items-center gap-6">
                            <Skeleton className="w-20 h-20 rounded-3xl bg-white/20" />
                            <div className="space-y-3 w-full">
                                <Skeleton className="h-8 w-64 bg-white/20" />
                                <Skeleton className="h-4 w-96 bg-white/20" />
                            </div>
                        </div>
                    </div>

                    <CardContent className="p-8 md:p-12 space-y-10">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">

                            {/* Left Skeleton */}
                            <div className="space-y-6">
                                <div className="space-y-2">
                                    <Skeleton className="h-4 w-48" />
                                    <Skeleton className="h-4 w-full" />
                                    <Skeleton className="h-4 w-3/4" />
                                </div>

                                <div className="p-8 rounded-[2rem] border border-dashed space-y-4">
                                    <Skeleton className="h-8 w-64" />
                                    <Skeleton className="h-4 w-40" />
                                    <Skeleton className="h-4 w-52" />
                                </div>
                            </div>

                            {/* Right Skeleton */}
                            <div className="space-y-6">
                                <Skeleton className="h-4 w-40" />

                                <div className="space-y-4">
                                    <div className="p-5 rounded-2xl border space-y-2">
                                        <Skeleton className="h-4 w-48" />
                                        <Skeleton className="h-4 w-full" />
                                        <Skeleton className="h-4 w-5/6" />
                                    </div>

                                    <div className="p-5 rounded-2xl border space-y-2">
                                        <Skeleton className="h-4 w-32" />
                                        <Skeleton className="h-4 w-full" />
                                    </div>
                                </div>

                                <Skeleton className="h-14 w-full rounded-2xl" />
                            </div>

                        </div>
                    </CardContent>

                    <div className="p-4 border-t text-center">
                        <Skeleton className="h-3 w-24 mx-auto" />
                    </div>

                </Card>
            </div>
        );
    }

    return (
        <div className="w-full space-y-6">
            <Card className="w-full shadow-xl bg-white dark:bg-slate-900 overflow-hidden border p-0 border-slate-100 dark:border-slate-800">
                {/* Header Section - Full Width */}
                <div className="bg-slate-900 dark:bg-blue-600 p-10 text-white relative overflow-hidden">
                    <div className="absolute inset-0 opacity-10 pointer-events-none" style={{ backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`, backgroundSize: '24px 24px' }} />
                    <div className="relative z-10 flex flex-col md:flex-row items-center gap-6 text-center md:text-left">
                        <div className="w-20 h-20 bg-white/10 backdrop-blur-md rounded-3xl flex items-center justify-center border border-white/20 shrink-0">
                            <ShieldAlert className="w-10 h-10 text-white" />
                        </div>
                        <div className="space-y-1">
                            <h2 className="text-3xl font-black uppercase tracking-tight">Pendaftaran Kolektif</h2>
                            <p className="text-blue-100 text-sm font-medium opacity-80 uppercase tracking-widest">Akses pendaftaran mandiri dibatasi oleh sistem</p>
                        </div>
                    </div>
                </div>

                <CardContent className="p-8 md:p-12 space-y-10">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                        {/* Kolom Kiri: Info Utama PIC */}
                        <div className="space-y-6">
                            <div className="space-y-2">
                                <h3 className="text-sm font-black uppercase tracking-[0.2em] text-blue-600 dark:text-blue-400">Penanggung Jawab (PIC)</h3>
                                <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed font-medium">
                                    Akun Anda telah di-import ke dalam sistem secara berkelompok. Segala bentuk perubahan paket dan status pembayaran dikelola sepenuhnya oleh:
                                </p>
                            </div>

                            <div className="bg-slate-50 dark:bg-slate-800/50 p-8 rounded-[2rem] border border-dashed border-slate-200 dark:border-slate-700 relative overflow-hidden group">
                                <div className="absolute -right-6 -bottom-6 opacity-5">
                                    <UserCheck className="w-32 h-32" />
                                </div>
                                <div className="relative z-10 space-y-4">
                                    <div className="space-y-1">
                                        <p className="text-2xl font-black text-slate-900 dark:text-white uppercase leading-tight tracking-tighter">
                                            {picProfile?.full_name || "Petugas Institusi"}
                                        </p>
                                        <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400">
                                            <Landmark className="w-4 h-4" />
                                            <p className="text-xs font-bold uppercase tracking-widest leading-none">
                                                {picProfile?.institution || "Institusi Terdaftar"}
                                            </p>
                                        </div>
                                    </div>

                                    {picProfile?.email && (
                                        <div className="flex items-center gap-2 text-slate-400">
                                            <Mail className="w-4 h-4" />
                                            <p className="text-xs font-mono">{picProfile.email}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Kolom Kanan: Instruksi & Navigasi */}
                        <div className="space-y-6">
                            <div className="space-y-2">
                                <h3 className="text-sm font-black uppercase tracking-[0.2em] text-slate-400">Instruksi Lanjutan</h3>
                                <div className="space-y-4">
                                    <div className="flex items-start gap-4 p-5 bg-blue-50 dark:bg-blue-900/20 rounded-2xl border border-blue-100 dark:border-blue-800">
                                        <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                                        <div className="space-y-1">
                                            <p className="text-xs font-black text-blue-800 dark:text-blue-300 uppercase">Mengapa saya melihat ini?</p>
                                            <p className="text-xs text-blue-700 dark:text-blue-400 font-medium leading-relaxed italic">
                                                Sistem mendeteksi bahwa pendaftaran Anda merupakan bagian dari delegasi institusi. Untuk menjaga validitas data kolektif, pendaftaran mandiri dinonaktifkan.
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-4 p-5 bg-slate-50 dark:bg-slate-800/40 rounded-2xl border border-slate-100 dark:border-slate-800">
                                        <UserCheck className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" />
                                        <div className="space-y-1">
                                            <p className="text-xs font-black text-slate-800 dark:text-slate-200 uppercase">Cetak Kartu</p>
                                            <p className="text-xs text-slate-500 dark:text-slate-400 font-medium leading-relaxed">
                                                Kartu bisa di unduh langsung oleh pic jika sudah menyelesaikan kewajiban pembayaran secara kolektif.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <Button
                                variant="outline"
                                className="w-full rounded-2xl border-slate-200 dark:border-slate-700 h-14 font-black text-xs uppercase tracking-[0.2em] hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                                onClick={() => navigate("/")}
                            >
                                <ArrowLeft className="w-4 h-4 mr-2" /> Kembali ke Dashboard Utama
                            </Button>
                        </div>
                    </div>
                </CardContent>

                <div className="bg-slate-50 dark:bg-slate-800/50 p-4 text-center border-t border-slate-100 dark:border-slate-800">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.5em]">Konaspi XII</p>
                </div>
            </Card>
        </div>
    );
};